import { useEffect } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createClinicLocation,
  createClinicLocationManager,
  getClinicLocation,
  getClinicLocationManager,
  getClinicLocationsList,
  getManagerWideArea,
  updateClinicLocation,
  updateClinicLocationManager,
  deleteClinicLocationManager,
  type ClinicLocationManagerStorePayload,
  type ClinicLocationManagerUpdatePayload,
  getClinicLocationMassager,
  updateClinicLocationMassager,
  deleteClinicLocationMassager,
  type ClinicLocationMassagerUpdatePayload,
  type ClinicLocationStorePayload,
  type ClinicLocationUpdatePayload,
  type ClinicLocationsListParams,
} from '@/services/admin/clinic-locations';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE, isNotFoundError } from '@/utils/apiError';

export const useAdminClinicLocationsListQuery = (
  siteType: string | null,
  params: ClinicLocationsListParams
) => {
  const toast = useAppToast();
  const query = useQuery({
    queryKey: ['admin', 'clinic-locations', 'list', siteType, params],
    queryFn: () => getClinicLocationsList(siteType!, params),
    enabled: !!siteType,
    /** Override global staleTime: refetch on every 検索 click. */
    staleTime: 0,
  });
  useEffect(() => {
    if (query.isError && query.error) {
      toast.error(getApiErrorMessage(query.error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [query.isError, query.error, toast]);
  return query;
};

/** Parse id from URL (supports '233', 233, or 'cl233' from legacy links). */
export function parseClinicLocationId(id: string | number | null): number | null {
  if (id == null) return null;
  if (typeof id === 'number') return Number.isNaN(id) ? null : id;
  const s = String(id).trim();
  const num = parseInt(s.replace(/^cl/i, ''), 10);
  return Number.isNaN(num) ? null : num;
}

export const useCreateClinicLocationMutation = () => {
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: ({
      siteType,
      payload,
    }: {
      siteType: string;
      payload: ClinicLocationStorePayload;
    }) => createClinicLocation(siteType, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'clinics'] });

      queryClient.invalidateQueries({
        queryKey: ['clinic-detail', String(variables.payload.clinic_id)],
      });
      queryClient.invalidateQueries({
        queryKey: ['admin', 'clinic-locations'],
      });
      toast.success('更新しました。');
    },
    onError: err => {
      toast.error(getApiErrorMessage(err, '拠点の作成に失敗しました。'));
      // Do not invalidate clinic options here: refetch can retrigger form sync effects and clear user input.
    },
  });
};

export const useClinicLocationQuery = (
  siteType: string | null,
  id: string | number | null,
  options?: {
    skipErrorToast?: boolean;
    refetchOnMount?: boolean | 'always';
    showOutsidePeriod?: boolean;
  }
) => {
  const toast = useAppToast();
  const numId = parseClinicLocationId(id);
  const query = useQuery({
    queryKey: ['admin', 'clinic-locations', 'show', siteType, numId, !!options?.showOutsidePeriod],
    queryFn: () =>
      getClinicLocation(siteType!, numId!, {
        showOutsidePeriod: !!options?.showOutsidePeriod,
      }),
    enabled: !!siteType && numId != null,
    refetchOnMount: options?.refetchOnMount,
    placeholderData: keepPreviousData,
  });
  useEffect(() => {
    if (!options?.skipErrorToast && query.isError && query.error) {
      if (isNotFoundError(query.error)) return;
      toast.error(getApiErrorMessage(query.error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [query.isError, query.error, toast, options?.skipErrorToast]);
  return query;
};

export const useUpdateClinicLocationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      siteType,
      id,
      payload,
    }: {
      siteType: string;
      id: number;
      payload: ClinicLocationUpdatePayload;
    }) => updateClinicLocation(siteType, id, payload),
    onSuccess: _ => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'clinics'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'clinic-locations'] });
      queryClient.invalidateQueries({
        queryKey: ['clinic-detail'],
      });
    },
  });
};

/** Wide area rows for 保険者別番号 prefill when selecting a massager on create manager form. */
export const useGetManagerWideAreaQuery = (
  siteType: string | null,
  clinicLocationId: number | null,
  massagerId: number | null
) => {
  const toast = useAppToast();
  const query = useQuery({
    queryKey: ['admin', 'clinic-locations', clinicLocationId, 'wide-area', massagerId],
    queryFn: () => getManagerWideArea(siteType!, clinicLocationId!, massagerId!),
    enabled: !!siteType && !!clinicLocationId && (massagerId ?? 0) > 0,
  });
  useEffect(() => {
    if (query.isError && query.error) {
      toast.error(getApiErrorMessage(query.error, '保険者別番号の取得に失敗しました。'));
    }
  }, [query.isError, query.error, toast]);
  return query;
};

export const useClinicLocationManagerQuery = (
  siteType: string | null,
  clinicLocationId: number | null,
  managerId: number | null
) => {
  const toast = useAppToast();
  const query = useQuery({
    queryKey: ['admin', 'clinic-locations', clinicLocationId, 'managers', managerId],
    queryFn: () => getClinicLocationManager(siteType!, clinicLocationId!, managerId!),
    enabled: !!siteType && !!clinicLocationId && (managerId ?? 0) > 0,
  });
  useEffect(() => {
    if (query.isError && query.error) {
      // 404: screen shows inline state; avoid toast (e.g. after update 404 + invalidate refetch).
      if (isNotFoundError(query.error)) return;
      toast.error(getApiErrorMessage(query.error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [query.isError, query.error, toast]);
  return query;
};

export const useCreateClinicLocationManagerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      siteType,
      clinicLocationId,
      payload,
    }: {
      siteType: string;
      clinicLocationId: number;
      payload: ClinicLocationManagerStorePayload;
    }) => createClinicLocationManager(siteType, clinicLocationId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          'admin',
          'clinic-locations',
          'show',
          variables.siteType,
          variables.clinicLocationId,
        ],
      });
      // Success toast is handled by the page.
    },
    onError: () => {
      // Error toast is handled by the page to keep a single fixed message.
    },
  });
};

export const useUpdateClinicLocationManagerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      siteType,
      clinicLocationId,
      managerId,
      payload,
    }: {
      siteType: string;
      clinicLocationId: number;
      managerId: number;
      payload: ClinicLocationManagerUpdatePayload;
    }) => updateClinicLocationManager(siteType, clinicLocationId, managerId, payload),
    meta: { suppressNotFoundToast: true },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          'admin',
          'clinic-locations',
          'show',
          variables.siteType,
          variables.clinicLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          'admin',
          'clinic-locations',
          variables.clinicLocationId,
          'managers',
          variables.managerId,
        ],
      });
    },
    onError: err => {
      if (isNotFoundError(err)) {
        return;
      }
      // Error toast is handled by the page to keep a single fixed message.
    },
  });
};

export const useDeleteClinicLocationManagerMutation = () => {
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: ({
      siteType,
      clinicLocationId,
      managerId,
    }: {
      siteType: string;
      clinicLocationId: number;
      managerId: number;
    }) => deleteClinicLocationManager(siteType, clinicLocationId, managerId),
    meta: { suppressNotFoundToast: true },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          'admin',
          'clinic-locations',
          'show',
          variables.siteType,
          variables.clinicLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ['admin', 'clinic-locations', variables.clinicLocationId, 'managers'],
      });
      toast.success('拠点施術者を削除しました。');
    },
    onError: (err, variables) => {
      if (isNotFoundError(err)) {
        queryClient.invalidateQueries({
          queryKey: [
            'admin',
            'clinic-locations',
            'show',
            variables.siteType,
            variables.clinicLocationId,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: ['admin', 'clinic-locations', variables.clinicLocationId, 'managers'],
        });
        toast.success('拠点施術者を削除しました。');
      } else {
        toast.error(getApiErrorMessage(err, '削除に失敗しました。'));
      }
    },
  });
};

/** Single GET returns { A2?, R2? } for the massager at this 拠点 (anchor = clinic_location_massagers id). */
export const useClinicLocationMassagerQuery = (
  siteType: string | null,
  clinicLocationId: number | null,
  clinicLocationMassagerId: number | null
) => {
  const query = useQuery({
    queryKey: [
      'admin',
      'clinic-locations',
      clinicLocationId,
      'massagers',
      clinicLocationMassagerId,
    ],
    queryFn: () =>
      getClinicLocationMassager(siteType!, clinicLocationId!, clinicLocationMassagerId!),
    enabled: !!siteType && !!clinicLocationId && (clinicLocationMassagerId ?? 0) > 0,
  });

  return query;
};

export const useUpdateClinicLocationMassagerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      siteType,
      clinicLocationId,
      massagerId,
      payload,
    }: {
      siteType: string;
      clinicLocationId: number;
      massagerId: number;
      payload: ClinicLocationMassagerUpdatePayload;
    }) => updateClinicLocationMassager(siteType, clinicLocationId, massagerId, payload),
    meta: { suppressNotFoundToast: true },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          'admin',
          'clinic-locations',
          'show',
          variables.siteType,
          variables.clinicLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          'admin',
          'clinic-locations',
          variables.clinicLocationId,
          'massagers',
          variables.massagerId,
        ],
      });
      queryClient.invalidateQueries({ queryKey: ['clinic-detail'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'massagers'] });
    },
    onError: () => {
      // Error handling moved to component
    },
  });
};

export const MASSAGER_DELETE_VISIT_DATA_ERROR =
  'すでにこの拠点の設定期間内で訪問データが登録されています。施術者編集を行う場合は、対象拠点の訪問データを削除してから再度実行してください。';

export const useDeleteClinicLocationMassagerMutation = () => {
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: ({
      siteType,
      clinicLocationId,
      massagerId,
    }: {
      siteType: string;
      clinicLocationId: number;
      massagerId: number;
    }) => deleteClinicLocationMassager(siteType, clinicLocationId, massagerId),
    meta: { suppressNotFoundToast: true },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          'admin',
          'clinic-locations',
          'show',
          variables.siteType,
          variables.clinicLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ['admin', 'clinic-locations', variables.clinicLocationId, 'massagers'],
      });
      queryClient.invalidateQueries({ queryKey: ['clinic-detail'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'massagers'] });
      toast.success('拠点施術者を削除しました。');
    },
    onError: (err, variables) => {
      if (isNotFoundError(err)) {
        queryClient.invalidateQueries({
          queryKey: [
            'admin',
            'clinic-locations',
            'show',
            variables.siteType,
            variables.clinicLocationId,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: ['admin', 'clinic-locations', variables.clinicLocationId, 'massagers'],
        });
        toast.success('拠点施術者を削除しました。');
        return;
      }
      const message = getApiErrorMessage(err, '削除に失敗しました。');
      if (message === MASSAGER_DELETE_VISIT_DATA_ERROR) {
        // Page handles redirect + banner; suppress toast to avoid duplicate
        return;
      }
      toast.error(message);
    },
  });
};
